/**
 * The authenticated frame: identity, sign-out, and the §11.2 banner above
 * whatever the route renders.
 *
 * The banner lives here rather than on a page so it cannot be escaped by
 * navigating away from the page that carries it.
 */

import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SyncBanner } from './components/SyncBanner.js';
import { NavMenu } from './components/NavMenu.js';
import { NewEntryMenu } from './components/NewEntryMenu.js';
import { ProfileMenu } from './components/ProfileMenu.js';
import { TodoProvider } from './components/TodoProvider.js';
import { TodoBadge } from './components/TodoBadge.js';
import { useAuth, useKeepsGoods } from './auth/AuthProvider.js';

/**
 * One nav item, and whether the screen showing belongs to it.
 *
 * A PREFIX LIST RATHER THAN NavLink, because several destinations own more
 * than one route and NavLink only knows its own. Entries owns the editor at
 * `/journals/:id`; Invoices owns Matching at `/clearings`, which is its second
 * tab and not a place of its own; Reports owns its drill-downs. An indicator
 * that went dark the moment the operator opened an entry would be telling them
 * they had left the section they are working in.
 *
 * `aria-current="page"` as well as a class: the underline is the visible half
 * and this is the half a screen reader gets.
 */
function NavItem({ to, label, owns, badge }: { to: string; label: string; owns?: string[]; badge?: ReactNode }) {
  const { pathname } = useLocation();
  /*
   * THE TRAILING SLASH IS WHAT MAKES HOME BEHAVE, and it is easy to lose.
   *
   * A bare `pathname.startsWith(prefix)` marks Home current on every screen in
   * the application, because every path starts with "/" -- an indicator that is
   * always right is no indicator. Requiring `${prefix}/` fixes that without a
   * special case: Home's only match is the exact one, since nothing starts with
   * "//". A `to === '/'` branch was written here first and was dead code; a
   * mutation that should have killed the suite passed, which is how it was
   * found.
   */
  const here = [to, ...(owns ?? [])].some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

  return (
    <Link to={to} className={here ? 'is-current' : undefined} aria-current={here ? 'page' : undefined}>
      {label}
      {badge}
    </Link>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { can } = useAuth();
  const keepsGoods = useKeepsGoods();

  return (
    /*
      The To Do list, read once for the frame: the Home tab counts it on every
      screen and Home lists it, so both read it from here (TodoProvider).
    */
    <TodoProvider>
      <SyncBanner />
      {/*
        THE BAR SITS OUTSIDE <main>, AND THAT IS THE POINT.

        It used to be inside it, so it inherited whatever width cap the
        screen asked for -- `main:has(.settings)` is 68rem, `main:has(>
        .report)` is 52rem -- and the twelve nav links wrapped inside
        whatever was left. The bar therefore had a DIFFERENT SHAPE ON
        EVERY SCREEN, and on the narrowest it wrapped onto two rows. The
        operator reported that from Settings on 2026-09-20.

        Chrome is not content. Out here it gets one cap of its own
        (`.header` in styles.css), so it looks the same wherever you are.
        Since 2026-09-24 every screen is the bar's width too (90rem since
        2026-09-25, 72rem before), so
        the two line up everywhere.
      */}
      <header className="row header">
        {/* The screens an operator moves between; rarer ones live in the profile menu. */}
        {/*
          The SAME list, in whichever form fits. NavMenu renders the nav and
          a button beside it; styles.css shows one or the other. The twelve
          destinations are still declared once, here, so a screen added to
          the bar cannot go missing from the phone.
        */}
        {/*
          THE COUNT IS ON HOME'S TAB, AND ON THE MENU BUTTON THAT HIDES IT.
          The operator asked for it where it is seen from every screen
          (2026-09-24), and on a phone the tabs are behind Menu, so the
          button carries it too. Both read the one list (TodoProvider).
        */}
        <NavMenu badge={<TodoBadge />}>
          {/*
            "Entries", not "Queue". The screen is every journal entry the
            tenant has -- its tabs are Uncategorized, Draft, Posted, Excluded
            and All -- and "Queue" promised only the bank backlog. The
            operator, who commissioned this, could not tell from the nav
            where invoices were supposed to go.
          */}
          <NavItem to="/" label="Home" badge={<TodoBadge />} />
          <NavItem to="/triage" label="Entries" owns={['/journals']} />
          {/*
            Invoices, with Matching as its second tab (2026-09-14). §6.3's
            matching is the step after posting an invoice, so it lives with
            the invoices rather than taking a menu item of its own.
          */}
          <NavItem to="/invoices" label="Invoices" owns={['/clearings']} />
          {/*
            §6.7 — Bills beside Invoices: the same question pointing the other
            way, and the two screens are deliberately the same shape.
          */}
          <NavItem to="/bills" label="Bills" />
          {/*
            Payroll (spec §18), beside Invoices: money in, then money out to
            the people it pays. Stage 1 is its employees and their W-4s.
          */}
          {/* Only to someone who may see payroll (0069): wages are kept from a bookkeeper by default. */}
          {can('view_payroll') && <NavItem to="/payroll" label="Payroll" />}
          {/* Beside Matching: both are the checks that follow coding. */}
          <NavItem to="/reconciliation" label="Reconcile" />
          {/*
            "Reports" sits where "Cash basis" was, and cash basis moved into
            it as a link. Two report destinations in one bar made the operator
            choose a report before choosing a period; the period is the same
            for all of them.
          */}
          <NavItem to="/reports" label="Reports" />
          <NavItem to="/accounts" label="Accounts" />
          {/* 0086: the goods a business keeps, once an account holds stock. */}
          {keepsGoods && <NavItem to="/items" label="Items" />}
          <NavItem to="/parties" label="Parties" />
          <NavItem to="/rules" label="Rules" />
          {/*
            §6.10 — beside Rules, not beside Reports. Both are standing
            instructions the operator writes once and the application applies
            afterwards, and neither is a report of anything.
          */}
          <NavItem to="/commitments" label="Commitments" />
          {/*
            Sync moved to the profile menu (the operator's decision,
            2026-09-13). It is a status page visited deliberately, and the
            banner above every screen already says when something is wrong.
          */}
        </NavMenu>
        {/*
          NEW ENTRY IS AN ACTION, so it is a button and not another nav link.
          It used to be a form folded inside the queue, which is why nobody
          could find it.

          A menu rather than one button, because the fields differ by what
          you are recording: an invoice has an invoice number and a general
          journal entry does not. WHAT IT PICKS IS A FORM, NOT A STORED TYPE
          -- there is no entry_type column and there should not be. An
          invoice already IS one: a debit to the receivables control account,
          a customer, an invoice number. Storing a second answer to "what is
          this" is how the two come to disagree.
        */}
        {/* New entries only for someone who may edit them (0069). */}
        {can('edit_entries') && <NewEntryMenu />}
        {/*
          The address and Sign out moved into a panel behind an icon. They
          were two items of header width spent on a fact one person already
          knows. The panel now also holds Settings and Sync, the two screens
          visited rarely and on purpose.
        */}
        <ProfileMenu />
      </header>
      <main>
        {children}
      </main>
    </TodoProvider>
  );
}
